import { placeholderPayload } from '@components/AccountAllocationGrid/placeholderMatrix';
import type { AccountAllocationGridReport } from '@components/AccountAllocationGrid/types';
import {
  allocationCellKey,
  parseAllocationCellKey,
} from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import type { SubAccountCustomAllocationInput } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';

// The context is posted whole on every change, so each id is written once and
// cells are bare numbers to keep the payload small.
export type StagedAllocationGrid = {
  subAccountIds: string[];
  financialProductIds: string[];
  fractions: (number | null)[][];
};

export type StagedAccountAllocations = {
  householdClientGroupId: string;
  portfolioReportId: string;
  matrix: StagedAllocationGrid;
  lockedCells: [row: number, column: number][];
  stagedAt: string;
};

export type StagedAccountAllocationBaseline = {
  portfolioReportId: string;
  fingerprint: string;
  capturedAt: string;
};

type StageAccountAllocationsInput = {
  householdClientGroupId: string;
  report: AccountAllocationGridReport;
  matrix: SubAccountCustomAllocationInput[];
  lockedCells: Iterable<string>;
  stagedAt: Date;
};

const toGrid = (
  matrix: SubAccountCustomAllocationInput[],
): StagedAllocationGrid => {
  const financialProductIds = [
    ...new Set(
      matrix.flatMap(({ overrides }) =>
        overrides.map(({ financialProductId }) => financialProductId),
      ),
    ),
  ];
  const columnByFinancialProductId = new Map(
    financialProductIds.map((financialProductId, column) => [
      financialProductId,
      column,
    ]),
  );

  return {
    subAccountIds: matrix.map(({ subAccountId }) => subAccountId),
    financialProductIds,
    fractions: matrix.map(({ overrides }) => {
      const row: (number | null)[] = financialProductIds.map(() => null);
      for (const {
        financialProductId,
        customExpectedPercentage,
      } of overrides) {
        const column = columnByFinancialProductId.get(financialProductId);
        if (column !== undefined) row[column] = customExpectedPercentage;
      }
      return row;
    }),
  };
};

const toGridPosition = (
  grid: StagedAllocationGrid,
  key: string,
): [row: number, column: number] => {
  const { subAccountId, financialProductId } = parseAllocationCellKey(key);
  const row = grid.subAccountIds.indexOf(subAccountId);
  const column = grid.financialProductIds.indexOf(financialProductId);
  if (row === -1 || column === -1 || grid.fractions[row][column] === null) {
    throw new Error(`Cannot lock ${key}: it is not a staged cell`);
  }
  return [row, column];
};

const compareIds = (left: string, right: string) =>
  Number(left > right) - Number(left < right);

const fnv1a = (text: string) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

export const stagedAccountAllocations = ({
  householdClientGroupId,
  report,
  matrix,
  lockedCells,
  stagedAt,
}: StageAccountAllocationsInput) => {
  const grid = toGrid(matrix);

  return {
    householdClientGroupId,
    portfolioReportId: report.id,
    matrix: grid,
    lockedCells: [...lockedCells].map((key) => toGridPosition(grid, key)),
    stagedAt: stagedAt.toISOString(),
  } satisfies StagedAccountAllocations;
};

export const unpackStagedAccountAllocations = ({
  matrix,
  lockedCells,
}: Pick<StagedAccountAllocations, 'matrix' | 'lockedCells'>) => ({
  matrix: matrix.subAccountIds.map(
    (subAccountId, row): SubAccountCustomAllocationInput => ({
      subAccountId,
      overrides: matrix.financialProductIds.flatMap(
        (financialProductId, column) => {
          const customExpectedPercentage = matrix.fractions[row][column];
          return customExpectedPercentage === null
            ? []
            : [{ financialProductId, customExpectedPercentage }];
        },
      ),
    }),
  ),
  lockedCells: lockedCells.map(([row, column]) =>
    allocationCellKey(
      matrix.subAccountIds[row],
      matrix.financialProductIds[column],
    ),
  ),
});

// Sorted first: the report's order is not guaranteed between fetches, and a
// reorder must not read as the allocation having moved.
export const accountAllocationFingerprint = (
  report: AccountAllocationGridReport,
) =>
  fnv1a(
    JSON.stringify(
      [...placeholderPayload(report)]
        .sort((left, right) =>
          compareIds(left.subAccountId, right.subAccountId),
        )
        .map(({ subAccountId, overrides }) => [
          subAccountId,
          [...overrides]
            .sort((left, right) =>
              compareIds(left.financialProductId, right.financialProductId),
            )
            .map(({ financialProductId, customExpectedPercentage }) => [
              financialProductId,
              customExpectedPercentage,
            ]),
        ]),
    ),
  );

export const stagedAccountAllocationBaseline = (
  report: AccountAllocationGridReport,
  capturedAt: Date,
) =>
  ({
    portfolioReportId: report.id,
    fingerprint: accountAllocationFingerprint(report),
    capturedAt: capturedAt.toISOString(),
  }) satisfies StagedAccountAllocationBaseline;
